/**
 * The on-disk project format: version, migration ladder, and the one encoder/decoder pair
 * the whole app uses. Ported from `native/PadLab/Model/ProjectSchema.swift`.
 *
 * A file written by any released version must load, or fail with a message the user can
 * act on. It must never silently lose data — which is why migration runs on the raw JSON
 * object rather than on a decoded `Project`: a v1 file may not decode into a v3 shape at
 * all, and a decode that "succeeds" by dropping unknown fields is data loss with a smile.
 *
 * Byte-for-byte compatibility with the Swift encoder is deliberate:
 *
 * - Keys are sorted, matching `JSONEncoder.OutputFormatting.sortedKeys`, so the same
 *   project hashes identically on both platforms and diffs are readable.
 * - Dates are ISO-8601 internet date-time with **no fractional seconds**. Swift's
 *   `.iso8601` strategy writes exactly that and its decoder rejects milliseconds, so
 *   `Date().toISOString()` would produce a file the iPhone app refuses to open. Decoding is
 *   deliberately more liberal than encoding: fractional seconds and numeric offsets are
 *   accepted and normalised.
 * - Slashes are not escaped (JavaScript never escapes them; Swift is told not to via
 *   `.withoutEscapingSlashes`).
 */

import {
  sanitiseProject,
  type ISODate,
  type Project,
} from './types';

export {
  EFFECT_PARAMETERS,
  effectKindParameter,
  effectKindParameters,
  effectKindDefaultValues,
  effectParameterClamp,
  effectParameterDenormalize,
  effectParameterFormat,
  effectParameterNormalize,
  clampSampleEdit,
  sanitiseProject,
  sanitisePattern,
  sanitiseStep,
  sanitiseTrack,
  normaliseEffectChain,
  normaliseEffectNode,
  nowISODate,
  toISODate,
} from './types';
export type { EffectParameter, EffectParameterScale } from './types';

/**
 * Bump whenever the shape of `Project` changes in a way older readers cannot understand,
 * and add a rung to `MIGRATIONS`.
 */
export const CURRENT_SCHEMA_VERSION = 1;

/** The oldest version this build still knows how to read. */
export const MINIMUM_SUPPORTED_SCHEMA_VERSION = 1;

export type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };
export type JsonObject = { [key: string]: JsonValue };

// MARK: - Errors

export type SchemaErrorKind = 'fromFuture' | 'tooOld' | 'corrupt';

/**
 * Every failure carries a sentence the user can read and a sentence telling them what to
 * do next, in the same voice as the native `ProjectSchema.MigrationError`.
 */
export class ProjectSchemaError extends Error {
  readonly kind: SchemaErrorKind;
  readonly recoverySuggestion: string;
  readonly foundVersion?: number;

  constructor(
    kind: SchemaErrorKind,
    message: string,
    recoverySuggestion: string,
    foundVersion?: number,
  ) {
    super(message);
    this.name = 'ProjectSchemaError';
    this.kind = kind;
    this.recoverySuggestion = recoverySuggestion;
    if (foundVersion !== undefined) this.foundVersion = foundVersion;
  }

  static fromFuture(found: number): ProjectSchemaError {
    return new ProjectSchemaError(
      'fromFuture',
      `This project was saved by a newer version of PadLab (format ${found}; this build reads up to ${CURRENT_SCHEMA_VERSION}).`,
      // The native copy says "Update PadLab from the App Store". On the web there is no
      // store: the equivalent action is taking the waiting service-worker update.
      'Reload PadLab to install the latest version, then open the project again. The file has not been changed.',
      found,
    );
  }

  static tooOld(found: number): ProjectSchemaError {
    return new ProjectSchemaError(
      'tooOld',
      `This project uses a retired format (${found}); this build reads format ${MINIMUM_SUPPORTED_SCHEMA_VERSION} and later.`,
      'Open the project in the version of PadLab that created it and export a fresh backup.',
      found,
    );
  }

  static corrupt(): ProjectSchemaError {
    return new ProjectSchemaError(
      'corrupt',
      'This project file is damaged and could not be read.',
      'Open a recent backup, or import the project package you exported. Nothing has been overwritten.',
    );
  }
}

export function isProjectSchemaError(value: unknown): value is ProjectSchemaError {
  return value instanceof ProjectSchemaError;
}

// MARK: - Migration ladder

/**
 * One entry per version bump, keyed by the version being migrated *from*. There is exactly
 * one released format today, so the ladder is empty — it is wired up and exercised by
 * `migrate`, not a placeholder, and adding version 2 means adding `1: (object) => …` here.
 */
const MIGRATIONS: Readonly<Record<number, (object: JsonObject) => JsonObject>> = Object.freeze({});

/** Reads just the version field, which is all it takes to decide whether a file can open. */
export function readSchemaVersion(object: JsonObject): number {
  const raw = object['schemaVersion'];
  return typeof raw === 'number' && Number.isFinite(raw)
    ? Math.trunc(raw)
    : MINIMUM_SUPPORTED_SCHEMA_VERSION;
}

/** Applies every migration between `version` and the current one, in order. */
export function migrate(rawObject: JsonObject, version: number): JsonObject {
  if (version > CURRENT_SCHEMA_VERSION) throw ProjectSchemaError.fromFuture(version);
  if (version < MINIMUM_SUPPORTED_SCHEMA_VERSION) throw ProjectSchemaError.tooOld(version);

  let object = rawObject;
  let current = version;
  while (current < CURRENT_SCHEMA_VERSION) {
    const rung = MIGRATIONS[current];
    if (rung === undefined) {
      // No rung defined: treat the file as already current rather than looping for ever.
      current = CURRENT_SCHEMA_VERSION;
      break;
    }
    object = rung(object);
    current += 1;
  }

  object['schemaVersion'] = CURRENT_SCHEMA_VERSION;
  return object;
}

// MARK: - Dates

const ISO_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:?\d{2})$/;

/**
 * Canonicalises any reasonable ISO-8601 instant to the exact form Swift writes and reads:
 * UTC, second resolution, `Z` suffix. Anything unparseable falls back to `now`, because a
 * project that will not open because of a bad timestamp is a worse outcome than a project
 * whose "modified" date is approximate.
 */
export function normaliseISODate(value: unknown, fallback: Date = new Date()): ISODate {
  if (typeof value === 'string' && ISO_PATTERN.test(value)) {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) return `${parsed.toISOString().slice(0, 19)}Z`;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    // Foundation's `.secondsSince1970` strategy, in case a backup was written with it.
    const parsed = new Date(value * 1000);
    if (!Number.isNaN(parsed.getTime())) return `${parsed.toISOString().slice(0, 19)}Z`;
  }
  return `${fallback.toISOString().slice(0, 19)}Z`;
}

export function parseISODate(value: ISODate): Date | null {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function normaliseProjectDates(project: Project): void {
  project.createdAt = normaliseISODate(project.createdAt);
  project.modifiedAt = normaliseISODate(project.modifiedAt);
  if (project.deletedAt !== undefined) project.deletedAt = normaliseISODate(project.deletedAt);
  if (!Array.isArray(project.assets)) return;
  for (const asset of project.assets) asset.createdAt = normaliseISODate(asset.createdAt);
}

// MARK: - Encoding

/** Deep clone with object keys in sorted order — the JS half of `.sortedKeys`. */
function sortKeys(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortKeys);
  if (value === null || typeof value !== 'object') return value;
  const source = value as Record<string, unknown>;
  const sorted: Record<string, unknown> = {};
  for (const key of Object.keys(source).sort()) {
    const entry = source[key];
    if (entry === undefined) continue;
    sorted[key] = sortKeys(entry);
  }
  return sorted;
}

export interface EncodeOptions {
  prettyPrinted?: boolean;
}

/**
 * Serialises a project to the canonical `.padlab` JSON text. Stamps the current schema
 * version and normalises dates, so a project handed over from an older in-memory state
 * cannot be written in a shape the native app would reject.
 */
export function encodeProject(project: Project, options: EncodeOptions = {}): string {
  const copy = structuredClone(project);
  copy.schemaVersion = CURRENT_SCHEMA_VERSION;
  normaliseProjectDates(copy);
  const ordered = sortKeys(copy);
  return options.prettyPrinted === true
    ? JSON.stringify(ordered, null, 2)
    : JSON.stringify(ordered);
}

export function encodeProjectBytes(project: Project, options: EncodeOptions = {}): Uint8Array {
  return new TextEncoder().encode(encodeProject(project, options));
}

// MARK: - Decoding

function toObject(input: string | ArrayBuffer | ArrayBufferView): JsonObject {
  let text: string;
  if (typeof input === 'string') {
    text = input;
  } else if (input instanceof ArrayBuffer) {
    text = new TextDecoder().decode(input);
  } else {
    text = new TextDecoder().decode(input.buffer as ArrayBuffer);
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw ProjectSchemaError.corrupt();
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw ProjectSchemaError.corrupt();
  }
  return parsed as JsonObject;
}

/**
 * Decodes an already-parsed object — the path IndexedDB takes, since it stores structured
 * clones rather than text. Runs the migration ladder and always sanitises, so callers can
 * assume every value is in range.
 */
export function decodeProjectObject(object: JsonObject): Project {
  const version = readSchemaVersion(object);
  const migrated = migrate(object, version);

  if (typeof migrated['id'] !== 'string' || typeof migrated['name'] !== 'string') {
    throw ProjectSchemaError.corrupt();
  }
  if (!Array.isArray(migrated['banks'])) throw ProjectSchemaError.corrupt();

  // Sanitise first: it is what guarantees the arrays and sub-objects the date pass walks.
  const project = sanitiseProject(migrated as unknown as Project);
  normaliseProjectDates(project);
  return project;
}

export function decodeProject(input: string | ArrayBuffer | ArrayBufferView): Project {
  return decodeProjectObject(toObject(input));
}
